import {
  createSlice,
  createAsyncThunk,
  createAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import axios from "../../axios";
import { ApiState, ResetApiState } from "../../redux.type";

export interface BookingRequest {
  buyer: {
    Tin: string;
    LegalName: string;
    Phone?: string;
    Email?: string;
    City?: string;
    Region?: string;
    Wereda?: string;
  };
  rental: {
    BusId: string;
    Price: number;
    StartDate: string;
    EndDate: string;
  };
  paymentTerm?: string;
  salesType: string;
  departureCity: string;
  destinationCity: string;
  // modeOfPayment: string;
}

export interface BookingResponse {
  invoicePayload: {
    TransactionType: string;
    DocumentDetails: {
      Type: string;
      DocumentNumber: string;
      Date: string;
    };
    SourceSystem: {
      InvoiceCounter: number;
      SystemNumber: string;
      SystemType: string;
      CashierName: string;
      SalesPersonName: string;
    };
    SellerDetails: {
      City: string;
      Email: string;
      LegalName: string;
      Region: string;
      Wereda: string;
      Tin: string;
      VatNumber: string;
      HouseNumber: string | null;
      Locality: string | null;
      SubCity: string | null;
    };
    BuyerDetails: {
      IdType: string;
      City: string;
      Region: string;
      IdNumber: string;
      Country: string;
      Wereda: string;
      Phone: string;
      Email: string;
      Tin: string;
      LegalName: string;
    };
    ReferenceDetails: {
      PreviousIrn: string;
    };
    ValueDetails: {
      InvoiceCurrency: string;
      TaxValue: number;
      TotalValue: number;
      TransactionWithholdValue: number;
      IncomeWithholdValue: number;
      ExciseValue: number;
    };
    ItemList: Array<{
      LineNumber: number;
      NatureOfSupplies: string;
      ItemCode: string;
      ProductDescription: string;
      UnitPrice: number;
      Quantity: number;
      Unit: string;
      TaxCode: string;
      PreTaxValue: number;
      TaxAmount: number;
      TotalLineAmount: number;
    }>;
    PaymentDetails: {
      Mode: string;
      PaymentTerm: string;
    };
    Version: string;
  };
  invoiceResponse: {
    irn: string;
    ackDate: string;
    signedQR: string;
    status: string;
    documentNumber: string;
    signedInvoice: string;
    errorMessage: string | null;
  };
  rentData: {
    Tin: string;
    LegalName: string;
    Phone: string;
    Email: string;
    City: string;
    Region: string;
    Wereda: string;
    BusId: string;
    Price: number;
    StartDate: string;
    EndDate: string;
    DepartureCity: string;
    DestinationCity: string;
    paymentTerm: string;
    salesType: string;
    id: string;
    createdAt: string;
  };
}

export interface BookingListItem {
  id: string;
  Tin: string;
  LegalName: string;
  Phone: string;
  Email: string;
  City: string;
  Region: string;
  Wereda: string;
  BusId: string;
  Price: string;
  StartDate: string;
  EndDate: string;
  DepartureCity: string;
  DestinationCity: string;
  paymentTerm: string;
  salesType: string;
  paymentStatus: string;
  updatedAt: string;
  createdAt: string;
  ticket: {
    id: number;
    ticketNo: string;
    serialNo: number;
    bank: string;
    printed: boolean;
    loadingPrice: number;
    attended: boolean;
    passenger: string;
    seat: string;
    status: string;
    date: string;
    createdAt: string;
    irn: string;
    ackDate: string;
    signedQR: string;
    signedInvoice: string;
    InvoiceCounter: number;
    InvoiceResponse: string;
  };
}

const NAME = "b2bBooking";

export interface B2BBookingState {
  createBooking: ApiState<BookingResponse | null>;
  getBookings: ApiState<BookingListItem[]>;
  getBookingById: ApiState<BookingListItem | null>;
}

const initialState: B2BBookingState = {
  createBooking: ResetApiState(null),
  getBookings: ResetApiState([]),
  getBookingById: ResetApiState(null),
};

export const createBooking = createAsyncThunk<BookingResponse, BookingRequest>(
  `${NAME}/createBooking`,
  async (payload, thunkApi) => {
    try {
      const resp = await axios.post<BookingResponse>(
        "/invoice/credit_invoice",
        payload
      );
      if (resp.data) {
        return resp.data;
      }
      return thunkApi.rejectWithValue(resp.data);
    } catch (err: any) {
      return thunkApi.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getBookings = createAsyncThunk<BookingListItem[]>(
  `${NAME}/getBookings`,
  async (_, thunkApi) => {
    try {
      const resp = await axios.get<BookingListItem[]>("/invoice/get_rents");
      return resp.data || [];
    } catch (err: any) {
      return thunkApi.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getBookingById = createAsyncThunk<BookingListItem, string>(
  `${NAME}/getBookingById`,
  async (id, thunkApi) => {
    try {
      const resp = await axios.get<BookingListItem>(`/invoice/get_rent/${id}`);
      return resp.data;
    } catch (err: any) {
      return thunkApi.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const resetCreateBooking = createAction(`${NAME}/resetCreateBooking`);

function buildExtraReducers(builder: ActionReducerMapBuilder<B2BBookingState>) {
  builder
    // Create booking
    .addCase(createBooking.pending, (state) => {
      state.createBooking = {
        loading: true,
        payload: null,
        successful: false,
        error: null,
      };
    })
    .addCase(createBooking.fulfilled, (state, action) => {
      state.createBooking = {
        loading: false,
        payload: action.payload,
        successful: true,
        error: null,
      };
    })
    .addCase(createBooking.rejected, (state, action) => {
      state.createBooking = {
        loading: false,
        payload: null,
        successful: false,
        error: action.payload,
      };
    })

    // Get all bookings
    .addCase(getBookings.pending, (state) => {
      state.getBookings = {
        loading: true,
        payload: [],
        successful: false,
        error: null,
      };
    })
    .addCase(getBookings.fulfilled, (state, action) => {
      state.getBookings = {
        loading: false,
        payload: action.payload,
        successful: true,
        error: null,
      };
    })
    .addCase(getBookings.rejected, (state, action) => {
      state.getBookings = {
        loading: false,
        payload: [],
        successful: false,
        error: action.payload,
      };
    })

    // Get booking by ID
    .addCase(getBookingById.pending, (state) => {
      state.getBookingById = {
        loading: true,
        payload: null,
        successful: false,
        error: null,
      };
    })
    .addCase(getBookingById.fulfilled, (state, action) => {
      state.getBookingById = {
        loading: false,
        payload: action.payload,
        successful: true,
        error: null,
      };
    })
    .addCase(getBookingById.rejected, (state, action) => {
      state.getBookingById = {
        loading: false,
        payload: null,
        successful: false,
        error: action.payload,
      };
    })

    .addCase(resetCreateBooking, (state) => {
      state.createBooking = ResetApiState(null);
    });
}

const B2BBookingSlice = createSlice({
  name: NAME,
  initialState,
  reducers: {},
  extraReducers: (builder) => buildExtraReducers(builder),
});

export const B2BBookingActions = {
  createBooking,
  getBookings,
  getBookingById,
  resetCreateBooking,
};

export const B2BBookingReducer = B2BBookingSlice.reducer;
